/**
 * EmergencyPowerModule — opt-in 72h noodstroom intake for residential.
 *
 * MVP foundation. No automatic control. No DIY claims. Anything touching the
 * home installation routes to /api/quote-request with component_type=
 * emergency_power. Heavy electric loads + 72h target force the installer
 * route via backend `services/emergency_power_advice.py`.
 */
import { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, ShieldCheck, ChevronDown, ChevronUp, Plug, Wrench } from 'lucide-react';
import { trackFunnelEvent } from '@/utils/tracking';

const API = '/api';
const FLOW = 'emergency_power';

const PRIORITY_GROUPS = [
  { value: 'fridge', label: 'Koelkast' },
  { value: 'freezer', label: 'Vriezer' },
  { value: 'lights', label: 'Verlichting' },
  { value: 'router', label: 'Internet/router' },
  { value: 'phone_laptop', label: 'Telefoon/laptop laden' },
  { value: 'cooking_light', label: 'Koken (licht)' },
  { value: 'water_pump', label: 'Drinkwater / waterpomp' },
  { value: 'central_heating', label: 'CV / verwarmingspomp' },
  { value: 'heat_pump', label: 'Warmtepomp' },
  { value: 'induction_cooking', label: 'Inductiekookplaat' },
  { value: 'electric_boiler', label: 'Elektrische boiler' },
  { value: 'medical_equipment', label: 'Medische apparatuur' },
  { value: 'alarm_door', label: 'Alarm / poort / deur' },
  { value: 'workplace', label: 'Thuiswerkplek' },
  { value: 'other', label: 'Anders' },
];

const SCENARIO = [
  { value: 'short', label: 'Ja, voor korte uitval' },
  { value: '72h', label: 'Ja, voor 72 uur noodbedrijf' },
  { value: 'later', label: 'Misschien later' },
  { value: 'no', label: 'Nee' },
];
const DURATION = [
  { value: '2_4h', label: '2 – 4 uur' },
  { value: '8_12h', label: '8 – 12 uur' },
  { value: '24h', label: '24 uur' },
  { value: '72h', label: '72 uur' },
];
const GAS = [
  { value: 'yes', label: 'Ja, gas aanwezig' },
  { value: 'no', label: 'Nee, volledig elektrisch' },
  { value: 'unknown', label: 'Weet ik niet' },
];
const SOLUTION = [
  { value: 'power_station', label: 'Draagbare power station' },
  { value: 'generator', label: 'Aggregaat' },
  { value: 'fixed_backup', label: 'Vaste backup-oplossing' },
  { value: 'calculate_first', label: 'Eerst laten berekenen' },
  { value: 'unknown', label: 'Weet ik niet' },
];

const HEAVY = new Set(['heat_pump', 'induction_cooking', 'electric_boiler', 'airco', 'ev_charger', 'jacuzzi', 'central_heating']);
const INSTALLER_FORCING = new Set([...HEAVY, 'cv_heating_pump', 'alarm_door']);
const SMART_PLUG_FRIENDLY = new Set(['fridge', 'freezer', 'router', 'phone_laptop', 'lights', 'workplace', 'small_kitchen_appliance', 'small_devices']);
const COOKING_GROUPS = new Set(['cooking_light', 'induction_cooking', 'oven']);
const HEATING_GROUPS = new Set(['central_heating', 'heat_pump', 'electric_boiler']);
const GROUP_LABEL = Object.fromEntries([
  { value: 'fridge', label: 'Koelkast' },
  { value: 'freezer', label: 'Vriezer' },
  { value: 'lights', label: 'Verlichting' },
  { value: 'router', label: 'Internet/router' },
  { value: 'phone_laptop', label: 'Telefoon/laptop laden' },
  { value: 'workplace', label: 'Thuiswerkplek' },
  { value: 'cooking_light', label: 'Koken (licht)' },
  { value: 'small_kitchen_appliance', label: 'Kleine keukenapparaten' },
  { value: 'small_devices', label: 'Kleine losse apparaten' },
].map(o => [o.value, o.label]));

const SAFETY_BLOCKS = [
  'Sluit een aggregaat nooit zomaar aan op de woninginstallatie. Voor groepen, meterkast of automatische omschakeling is controle door een erkende installateur nodig.',
  'Een aggregaat moet veilig buiten worden gebruikt volgens de handleiding. Powersure geeft een eerste inschatting en zet installatievragen door naar een specialist.',
  'Dit is geen garantie op volledig off-grid gebruik. Het doel is een veilig noodscenario voor belangrijke functies.',
];

export default function EmergencyPowerModule({ intakeId, reportId, defaultEmail = '' }) {
  const [open, setOpen] = useState(false);
  const [scenario, setScenario] = useState('');
  const [groups, setGroups] = useState([]);
  const [hasGas, setHasGas] = useState('');
  const [duration, setDuration] = useState('');
  const [solution, setSolution] = useState('');
  const [form, setForm] = useState({
    name: '', email: defaultEmail, phone: '', street: '', house_number: '',
    postcode: '', city: '', context: '', consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submittedRequestId, setSubmittedRequestId] = useState('');

  const heavySelected = useMemo(() => groups.some(g => HEAVY.has(g)), [groups]);
  const installerForced = useMemo(
    () => groups.some(g => INSTALLER_FORCING.has(g)) || duration === '72h',
    [groups, duration]
  );
  const noGasHeatRisk = hasGas === 'no' && groups.some(g => ['cooking_light', 'induction_cooking', 'central_heating', 'heat_pump'].includes(g));

  // Smart-plug advice: only for light, plug-friendly groups. Empty list -> no block.
  const plugEligible = useMemo(() => groups.filter(g => SMART_PLUG_FRIENDLY.has(g)), [groups]);
  const showSmartPlugBlock = plugEligible.length > 0;

  // Meterbox-module / generator-input measurement advice mirror the backend
  // `emergency_power_advice.meterbox_module_required` logic so the UI stays
  // in sync without re-querying after each selection.
  const showMeterboxBlock = useMemo(() => (
    duration === '72h'
    || groups.includes('heat_pump')
    || (hasGas === 'no' && groups.some(g => COOKING_GROUPS.has(g) || HEATING_GROUPS.has(g)))
    || groups.some(g => INSTALLER_FORCING.has(g))
    || solution === 'fixed_backup'
  ), [groups, duration, hasGas, solution]);

  // Fire `emergency_power_smart_plug_suggested` once per page-load when the
  // smart-plug block becomes visible. Same idempotency pattern as elsewhere.
  const [plugSuggestedFired, setPlugSuggestedFired] = useState(false);
  if (showSmartPlugBlock && !plugSuggestedFired) {
    trackFunnelEvent('emergency_power_smart_plug_suggested', { flow: FLOW, intake_id: intakeId || '' });
    setPlugSuggestedFired(true);
  }

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next) trackFunnelEvent('emergency_power_view', { flow: FLOW, intake_id: intakeId || '' });
  };

  const onScenario = (v) => {
    setScenario(v);
    trackFunnelEvent('emergency_power_started', { flow: FLOW, intake_id: intakeId || '' });
  };

  const toggleGroup = (v) => {
    setGroups(prev => {
      const next = prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v];
      trackFunnelEvent('emergency_power_group_selected', { flow: FLOW, intake_id: intakeId || '' });
      return next;
    });
  };

  const ctaLabel = installerForced ? 'Vraag veilige installatieofferte' : 'Vraag advies aan';

  const submit = async () => {
    if (!form.consent) { toast.error('U moet eerst akkoord geven om verder te kunnen.'); return; }
    if (!form.name || !form.email || !form.phone || !form.postcode || !form.house_number) {
      toast.error('Vul a.u.b. naam, e-mail, telefoon, postcode en huisnummer in.');
      return;
    }
    setSubmitting(true);
    try {
      trackFunnelEvent('emergency_power_quote_started', { flow: FLOW, intake_id: intakeId || '' });
      const epData = {
        scenario,
        selected_priority_groups: groups,
        selected_groups: groups, // kept for backward-compat with backend advice helper
        duration_target: duration,
        emergency_duration_target: duration,
        has_gas: hasGas,
        cooking_need: groups.some(g => COOKING_GROUPS.has(g)),
        heating_need: groups.some(g => HEATING_GROUPS.has(g)),
        heat_pump_selected: groups.includes('heat_pump'),
        heavy_load_warning: groups.some(g => HEAVY.has(g)),
        smart_plug_suggestions: showSmartPlugBlock,
        smart_plug_eligible_groups: plugEligible,
        meterbox_module_required: showMeterboxBlock,
        generator_input_measurement_required: showMeterboxBlock,
        installer_required: installerForced || showMeterboxBlock || groups.some(g => HEAVY.has(g)),
        recommended_route: (showMeterboxBlock || installerForced) ? 'installer_check' : 'power_station',
        solution_preference: solution,
      };
      const body = {
        report_id: reportId || intakeId || 'emergency-power-direct',
        category: 'emergency_power',
        specialization: 'emergency_power',
        component_type: 'emergency_power',
        segment: 'residential',
        country: 'NL',
        name: form.name,
        email: form.email,
        phone: form.phone,
        street: form.street,
        house_number: form.house_number,
        postcode: form.postcode,
        city: form.city,
        context: form.context,
        consent: form.consent,
        emergency_power_data: epData,
      };
      const res = await axios.post(`${API}/quote-request`, body);
      if (res?.data?.success) {
        setSubmittedRequestId(res.data.request_id || '');
        trackFunnelEvent('emergency_power_quote_submitted', {
          flow: FLOW,
          intake_id: intakeId || '',
          report_id: res.data.request_id || '',
        });
        toast.success('Uw noodstroomaanvraag is ontvangen. U krijgt ook een bevestiging per e-mail.');
      } else {
        toast.error('Er ging iets mis. Probeer het opnieuw.');
      }
    } catch {
      toast.error('Er ging iets mis. Probeer het opnieuw.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submittedRequestId) {
    return (
      <section className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/60 p-5" data-testid="emergency-power-submitted">
        <h3 className="text-lg font-semibold text-emerald-800" style={{ fontFamily: 'Outfit' }}>
          Aanvraag ontvangen
        </h3>
        <p className="mt-1 text-sm text-emerald-900">
          Uw noodstroomaanvraag is opgeslagen onder referentie <code className="text-xs">{submittedRequestId.slice(0, 8)}</code>.
          Een specialist neemt contact met u op. U krijgt ook een bevestiging per e-mail.
        </p>
      </section>
    );
  }

  return (
    <section
      className="rounded-2xl border border-amber-200 bg-amber-50/40 p-5 sm:p-6 mt-8"
      data-testid="emergency-power-module"
    >
      <button
        type="button"
        onClick={toggleOpen}
        className="w-full flex items-center justify-between gap-3 text-left"
        data-testid="emergency-power-toggle"
      >
        <div>
          <div className="text-[11px] uppercase tracking-wider font-semibold text-amber-800">
            Noodstroom bij stroomuitval
          </div>
          <h3 className="text-lg sm:text-xl font-semibold text-amber-900 mt-1" style={{ fontFamily: 'Outfit' }}>
            72-uurs noodscenario voor uw woning
          </h3>
          <p className="mt-1 text-sm text-amber-900/85">
            Kies welke functies bij stroomuitval belangrijk zijn. Powersure maakt een eerste 72-uurs noodscenario en zet installatievragen veilig door naar een specialist.
          </p>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-amber-800 shrink-0" /> : <ChevronDown className="w-5 h-5 text-amber-800 shrink-0" />}
      </button>

      {!open ? null : (
        <div className="mt-5 space-y-5" data-testid="emergency-power-form">
          <Question label="Wilt u een noodstroomscenario voor uw woning?" testid="ep-q-scenario">
            {SCENARIO.map(o => (
              <Pill key={o.value} active={scenario === o.value} onClick={() => onScenario(o.value)} testid={`ep-scenario-${o.value}`}>{o.label}</Pill>
            ))}
          </Question>

          <Question label="Wat moet bij stroomuitval blijven werken?" testid="ep-q-groups">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {PRIORITY_GROUPS.map(o => (
                <Pill key={o.value} active={groups.includes(o.value)} onClick={() => toggleGroup(o.value)} testid={`ep-group-${o.value}`}>{o.label}</Pill>
              ))}
            </div>
          </Question>

          <Question label="Heeft uw woning gas om te koken of verwarmen?" testid="ep-q-gas">
            {GAS.map(o => (
              <Pill key={o.value} active={hasGas === o.value} onClick={() => setHasGas(o.value)} testid={`ep-gas-${o.value}`}>{o.label}</Pill>
            ))}
          </Question>

          <Question label="Hoe lang wilt u minimaal kunnen overbruggen?" testid="ep-q-duration">
            {DURATION.map(o => (
              <Pill key={o.value} active={duration === o.value} onClick={() => setDuration(o.value)} testid={`ep-duration-${o.value}`}>{o.label}</Pill>
            ))}
          </Question>

          <Question label="Welke oplossing wilt u overwegen?" testid="ep-q-solution">
            {SOLUTION.map(o => (
              <Pill key={o.value} active={solution === o.value} onClick={() => setSolution(o.value)} testid={`ep-solution-${o.value}`}>{o.label}</Pill>
            ))}
          </Question>

          {(heavySelected || installerForced || noGasHeatRisk) && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3" data-testid="emergency-power-heavy-warning">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-rose-700 mt-0.5 shrink-0" />
                <div className="text-sm text-rose-900 leading-snug">
                  <p className="font-semibold">Installateurcheck verplicht.</p>
                  <p className="mt-1">
                    Zware apparaten zoals warmtepomp, inductiekookplaat, boiler, airco, jacuzzi en EV-lader vragen altijd een aparte berekening. Powersure neemt deze niet automatisch mee als veilige noodstroomgroep. De aanvraag wordt doorgezet naar een erkende installateur.
                  </p>
                </div>
              </div>
            </div>
          )}

          {showSmartPlugBlock && (
            <div className="rounded-lg border border-sky-200 bg-sky-50 p-3" data-testid="emergency-power-smart-plug-block">
              <div className="flex items-start gap-2">
                <Plug className="w-4 h-4 text-sky-700 mt-0.5 shrink-0" />
                <div className="text-sm text-sky-900 leading-snug">
                  <p className="font-semibold">Slimme stekkers voor losse apparaten</p>
                  <p className="mt-1">
                    Voor losse apparaten kan een slimme meetstekker helpen om verbruik te meten of prioriteit te geven. Dit geldt niet voor zware of vaste installaties.
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1" data-testid="emergency-power-smart-plug-eligible">
                    {plugEligible.map(g => (
                      <span key={g} className="text-[12px] bg-white border border-sky-200 rounded-full px-2 py-0.5" data-testid={`emergency-power-plug-elig-${g}`}>
                        {GROUP_LABEL[g] || g}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {showMeterboxBlock && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="emergency-power-meterbox-block">
              <div className="flex items-start gap-2">
                <Wrench className="w-4 h-4 text-amber-800 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-900 leading-snug">
                  <p className="font-semibold">Meterkastmodule / installateurcheck</p>
                  <p className="mt-1">
                    Voor noodstroom via groepen of meterkast is een meet- en omschakelmodule nodig. Een installateur moet controleren hoe aggregaatstroom veilig wordt gemeten, geschakeld en gescheiden van het net.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="rounded-lg border border-amber-300 bg-white/70 p-3 space-y-2" data-testid="emergency-power-safety-copy">
            <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
              <ShieldCheck className="w-4 h-4" /> Veiligheid eerst
            </div>
            {SAFETY_BLOCKS.map((s, i) => (
              <p key={i} className="text-[13px] text-amber-900/90 leading-snug" data-testid={`emergency-power-safety-${i}`}>{s}</p>
            ))}
          </div>

          <div className="rounded-lg border border-border bg-white p-4 space-y-3" data-testid="emergency-power-quote-form">
            <h4 className="text-sm font-semibold text-foreground">Gegevens voor uw aanvraag</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field testid="ep-name" label="Naam" value={form.name} onChange={(v) => setForm(s => ({ ...s, name: v }))} required />
              <Field testid="ep-email" label="E-mail" type="email" value={form.email} onChange={(v) => setForm(s => ({ ...s, email: v }))} required />
              <Field testid="ep-phone" label="Telefoon" value={form.phone} onChange={(v) => setForm(s => ({ ...s, phone: v }))} required />
              <Field testid="ep-postcode" label="Postcode" value={form.postcode} onChange={(v) => setForm(s => ({ ...s, postcode: v }))} required />
              <Field testid="ep-street" label="Straat" value={form.street} onChange={(v) => setForm(s => ({ ...s, street: v }))} />
              <Field testid="ep-house" label="Huisnummer" value={form.house_number} onChange={(v) => setForm(s => ({ ...s, house_number: v }))} required />
              <Field testid="ep-city" label="Plaats" value={form.city} onChange={(v) => setForm(s => ({ ...s, city: v }))} />
            </div>
            <label className="block text-sm">
              <span className="text-foreground/80">Bericht (optioneel)</span>
              <textarea
                value={form.context}
                onChange={(e) => setForm(s => ({ ...s, context: e.target.value }))}
                rows={3}
                className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
                data-testid="ep-context"
              />
            </label>
            <label className="flex items-start gap-2 text-sm" data-testid="ep-consent-label">
              <input type="checkbox" checked={form.consent} onChange={(e) => setForm(s => ({ ...s, consent: e.target.checked }))} data-testid="ep-consent" />
              <span>Ik geef toestemming dat Powersure deze gegevens deelt met een erkende installateur voor het opstellen van een noodstroomofferte.</span>
            </label>
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              className="w-full btn-primary px-4 py-2.5 rounded-md text-sm font-semibold disabled:opacity-60"
              data-testid="emergency-power-submit"
            >
              {submitting ? 'Bezig…' : ctaLabel}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function Question({ label, testid, children }) {
  return (
    <div data-testid={testid}>
      <div className="text-sm font-medium text-foreground mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function Pill({ active, onClick, testid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'px-3 py-2 rounded-full text-sm border transition-colors',
        active ? 'bg-primary text-primary-foreground border-primary' : 'bg-white text-foreground border-border hover:border-primary/50',
      ].join(' ')}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function Field({ testid, label, value, onChange, type = 'text', required = false }) {
  return (
    <label className="block text-sm">
      <span className="text-foreground/80">{label}{required && ' *'}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
        data-testid={testid}
      />
    </label>
  );
}
